import { html, nothing } from 'omni-ui';
import { Om2FormFieldBase, SHARED_FIELD } from './common/base.js';
import { Om2TwoPaneSelect } from '../om2-two-pane-select.js';

Om2TwoPaneSelect.register();

/**
 * Render two pane select. Should be compatible with multiselect dropdown.
 */
export class Om2FormFieldTwoPane extends Om2FormFieldBase {
  static metadata = {
    config: {
      label: 'Two Pane-Select',
      overrides: {
        type: 'string',
        twopane: true,
      },
      fields: {
        id: SHARED_FIELD,
        title: SHARED_FIELD,
        asyncEnum: {
          title: 'External Options (Asynchronous)',
          type: 'boolean',
          default: false,
        },
        enum: {
          type: 'array',
          id: 'enum',
          title: 'Options',
          minRows: 1,
          content: [
            {
              type: 'column',
              content: [
                { type: 'ref', key: 'id', id: '__array_id', required: true },
              ],
            },
            {
              type: 'column',
              content: [{ type: 'ref', key: 'name', id: '__array_name' }],
            },
          ],
          removeButton: {
            class: 'is-medium no-border-background is-shadowless is-text',
            icon: {
              id: 'omni:interactive:remove',
              class: 'is-size-2',
            },
          },
          addButton: {
            class: 'is-outlined',
            icon: {
              id: 'omni:interactive:add',
              class: 'is-size-2',
            },
          },
          formModal: {
            fields: {
              __array_id: {
                type: 'string',
                title: 'Id',
                tooltip:
                  'The id uniquely identifies the radio button option and should not change.',
              },
              __array_name: {
                type: 'string',
                title: 'Name',
                tooltip:
                  'This is the "pretty" name that is displayed to the user',
              },
            },
            hideIf: { id: 'asyncEnum', op: '===', value: true },
            fullWidth: true,
          },
        },
        treeView: {
          title: 'Hierarchical/Tree View',
          type: 'boolean',
          default: false,
          tooltip:
            'Renders the options in a nested tree view. The Options URL should contain JSON with each option potentially containing a "children" key with an array of child options.',
          formModal: {
            hideIf: { id: 'asyncEnum', op: '!==', value: true },
          },
        },
        collapsible: {
          title: 'Collapsible options',
          type: 'boolean',
          default: false,
          tooltip:
            'Allow parent options in tree view to be collapsed and expanded',
          formModal: {
            hideIf: { id: 'treeView', op: '!==', value: true },
          },
        },
        enumURL: {
          title: 'Options URL',
          type: 'string',
          tooltip:
            'The URL from which to load options asyncronously. It must return properly formatted JSON or CSV with "id" and "name" keys/columns. To reference an external field (id = "my_field") in the url, use the syntax: %:my_field%',
          formModal: {
            hideIf: { id: 'asyncEnum', op: '!==', value: true },
            fullWidth: true,
          },
        },
        tooltip: SHARED_FIELD,
        class: SHARED_FIELD,
        leftPaneLabel: {
          title: 'Left Pane Label',
          type: 'string',
        },
        rightPaneLabel: {
          title: 'Right Pane Label',
          type: 'string',
        },
        leftPanePlaceholder: {
          title: 'Left Pane Placeholder',
          type: 'string',
        },
        rightPanePlaceholder: {
          title: 'Right Pane Placeholder',
          type: 'string',
        },
        searchPlaceholder: {
          title: 'Search Placeholder',
          type: 'string',
        },
        default: SHARED_FIELD,
        required: SHARED_FIELD,
        disabled: SHARED_FIELD,
        readonly: SHARED_FIELD,
        hasObjectValues: SHARED_FIELD,
      },
    },
  };

  render() {
    const { schema } = this;
    const changeHandler = (e, opts = {}) =>
      this.dispatchNewEvent('input', {
        detail: { name: this.key, value: e.target.value, ...opts },
      });
    return html`
      <om2-two-pane-select
        .options=${schema.treeView ? nothing : schema.enum}
        .optionsTree=${schema.treeView ? schema.enum : nothing}
        .name="${this.key}"
        .value=${this.data ?? schema.default ?? []}
        .leftPaneLabel=${schema.leftPaneLabel ?? ''}
        .rightPaneLabel=${schema.rightPaneLabel ?? ''}
        .leftPanePlaceholder=${schema.leftPanePlaceholder ?? ''}
        .rightPanePlaceholder=${schema.rightPanePlaceholder ?? ''}
        .searchPlaceholder=${schema.searchPlaceholder ?? ''}
        .addOptionFromSearch=${schema.addOptionFromSearch}
        .hasObjectValues=${schema.hasObjectValues}
        .asyncSearch=${schema.asyncSearch}
        .renderOption=${schema.renderOption ?? nothing}
        ?collapsible=${schema.collapsible}
        ?required=${schema.required}
        ?disabled=${schema.disabled ||
        schema.readonly ||
        this.context?.readonly ||
        this._isHidden()}
        idProperty=${schema.idProperty ?? 'id'}
        nameProperty=${schema.nameProperty ?? 'name'}
        childrenProperty=${schema.childrenProperty ?? nothing}
        @change=${changeHandler}
        @init-change=${e => changeHandler(e, { isAutoGenerated: true })}
      >
      </om2-two-pane-select>
    `;
  }
}

Om2FormFieldTwoPane.register('two-pane');
